export const queryMessages = {
  en: {
    navigation: 'Create Query',
    eyebrow: 'Queries',
    title: 'Create Query',
    description: 'Capture the minimum details required to start a new Query.',
    sections: {
      trip: 'Trip basics',
      agent: 'Agent',
      contact: 'Contact info',
      guest: 'Guest details',
      preferences: 'Preferences',
      additional: 'Additional',
    },
    transportationTypes: {
      private: 'Private (Pvt)',
      sic: 'Seat-in-coach (SIC)',
    },
    fields: {
      reservationExecutive: 'Reservation Executive',
      salesExecutive: 'Sales Executive',
      transportationType: 'Transportation type',
      hotelStarCategory: 'Hotel star category',
      mealPreference: 'Meal preference',
      specialRemarks: 'Special remarks',
      reservationCountry: 'Country',
      reservationTeam: 'Team',
      country: 'Destination country',
      currency: 'Query currency',
      startDate: 'Travel start date',
      endDate: 'Travel end date',
      travelDates: 'Travel dates',
      adultCount: 'No. of adults',
      childCount: 'No. of children',
      childAge: 'Age of child {number}',
      focCount: 'No. of FOC travellers',
      totalTravellers: 'Total travellers',
      numberOfNights: 'No. of nights',
      primaryTravellerName: 'Primary traveller name',
      primaryTravellerPhone: 'Primary traveller contact number',
      bookingType: 'Booking type',
      agentSearch: 'Search Agent',
      agent: 'Agent company',
      contactName: 'Contact name',
      contactEmail: 'Email',
      contactPhone: 'Phone',
      agentNote: 'Agent comment/note {number}',
    },
    placeholders: {
      reservationExecutive: 'Select an active Reservation Executive',
      reservationExecutiveSelf: 'Assign to myself',
      salesExecutive: 'Select an active Sales Executive',
      salesSearch: 'Search Sales Executives',
      reservationCountry: 'Select a country',
      reservationTeam: 'Select a team',
      country: 'Select a destination country',
      currency: 'Select a currency',
      agentSearch: 'Search by Agent name or code',
      agent: 'Select an active Agent',
      travelDates: 'Select travel date range',
      transportationType: 'Select a transportation type',
      hotelStarCategory: 'Select a hotel star category',
    },
    bookingTypes: {
      fit: 'FIT',
      group: 'Group',
      help: '1–10 travellers = FIT; 11 or more = Group.',
    },
    actions: {
      cancel: 'Cancel',
      create: 'Create Query',
      creating: 'Creating…',
      createAgent: 'Create new Agent',
      addSalesExecutive: 'Add',
      createAnother: 'Create another Query',
      retry: 'Retry',
      addAgentNote: 'Add another comment/note',
      removeAgentNote: 'Remove',
    },
    states: {
      loadingReservationExecutives: 'Loading Reservation Executives…',
      loadingCountries: 'Loading countries…',
      loadingAgents: 'Loading Agents…',
      resolvingSales: 'Resolving Sales ownership…',
      salesSelected: 'Sales Executive added.',
      noReservationExecutives: 'No eligible FIT/GROUP Reservation Executives are available.',
    },
    validation: {
      reservationExecutive: 'Reservation Executive is required.',
      country: 'Destination country is required.',
      currency: 'Select a valid three-letter currency.',
      agent: 'Agent company is required.',
      salesExecutive: 'Resolve and add a Sales Executive before creating the Query.',
      contactName: 'Query contact name is required.',
      startDate: 'Travel start date is required.',
      endDate: 'Travel end date is required.',
      dateRange: 'Travel end date must be on or after the start date.',
      adultCount: 'Number of adults must be a positive whole number.',
      childCount: 'Number of children must be zero or a positive whole number.',
      childAges: 'Enter an age from 0 to 17 for every child.',
      focCount: 'Number of FOC travellers must be zero or a positive whole number.',
      primaryTravellerName: 'Primary traveller name is required.',
      primaryTravellerPhone: 'Primary traveller contact number is required.',
      email: 'Contact email is required and must be valid.',
    },
    errors: {
      reservationExecutives: 'Reservation Executives could not be loaded.',
      reservationExecutiveRequired: 'Select an active Reservation Executive.',
      reservationExecutiveInvalid: 'The selected Reservation Executive or home team is no longer active.',
      create: 'Query could not be created. Your entered information has been kept.',
      countries: 'Active countries could not be loaded.',
      agents: 'Active Agents could not be loaded.',
      salesResolution: 'Sales ownership could not be resolved. Retry without losing your entries.',
      agentInactive: 'The selected Agent is inactive. Select another Agent.',
      agentNotFound: 'The selected Agent no longer exists.',
      countryInactive: 'The selected destination country is inactive.',
      countryNotDestination:
        'The selected country is not enabled as a Query destination.',
      countryNotFound: 'The selected destination country no longer exists.',
      countryTimezone: 'The selected country has an invalid timezone configuration.',
      forbidden: 'You do not have access to create or change this Query.',
      email: 'The Query contact email is invalid.',
      date: 'Enter a valid travel date.',
      dateRange: 'Travel end date must be on or after the start date.',
      contactName: 'Query contact name is required.',
      travellerCount: 'Total travellers must be a positive whole number.',
      validation: 'Check the Query details and try again.',
      versionConflict: 'This Query changed elsewhere. Refresh before editing again.',
      locked: 'Confirmed booking cannot be changed directly. Create an amendment instead.',
      notFound: 'Query was not found.',
    },
    messages: {
      created: 'Query created successfully: {number}',
    },
  },
  ja: {
    navigation: 'クエリ作成',
    eyebrow: 'クエリ',
    title: 'クエリを作成',
    description: '新しいクエリを開始するために必要な最小限の情報を入力します。',
    sections: {
      trip: '旅行基本情報',
      agent: 'エージェント',
      contact: '連絡先情報',
      guest: '旅行者情報',
      preferences: '希望条件',
      additional: '追加情報',
    },
    transportationTypes: {
      private: 'プライベート (Pvt)',
      sic: '混乗 (SIC)',
    },
    fields: {
      reservationExecutive: '予約担当者',
      salesExecutive: '営業担当者',
      transportationType: '交通手段',
      hotelStarCategory: 'ホテルスターカテゴリー',
      mealPreference: '食事の希望',
      specialRemarks: '特記事項',
      reservationCountry: '国',
      reservationTeam: 'チーム',
      country: '目的地の国',
      currency: 'クエリ通貨',
      startDate: '旅行開始日',
      endDate: '旅行終了日',
      travelDates: '旅行日程',
      travellerCount: '旅行者数',
      adultCount: '大人人数',
      childCount: '子供人数',
      childAge: '子供{number}の年齢',
      focCount: 'FOC人数',
      totalTravellers: '合計旅行者数',
      numberOfNights: '宿泊数',
      primaryTravellerName: '代表旅行者名',
      primaryTravellerPhone: '代表旅行者の連絡先番号',
      bookingType: '予約タイプ',
      agentSearch: 'エージェント検索',
      agent: 'エージェント会社',
      contactName: '連絡先名',
      contactEmail: 'メール',
      contactPhone: '電話',
      notes: 'メモ',
      agentNote: 'エージェントコメント・メモ {number}',
    },
    placeholders: {
      reservationExecutive: '有効な予約担当者を選択',
      reservationExecutiveSelf: '自分に割り当てる',
      salesExecutive: '有効な営業担当者を選択',
      salesSearch: '営業担当者を検索',
      reservationCountry: '国を選択',
      reservationTeam: 'チームを選択',
      country: '目的地の国を選択',
      currency: '通貨を選択',
      agentSearch: 'エージェント名またはコードで検索',
      agent: '有効なエージェントを選択',
      travelDates: '旅行日程を選択',
      transportationType: '交通手段を選択',
      hotelStarCategory: 'ホテルスターカテゴリーを選択',
    },
    bookingTypes: {
      fit: 'FIT',
      group: 'グループ',
      help: '1～10名はFIT、11名以上はグループです。',
    },
    actions: {
      cancel: 'キャンセル',
      create: 'クエリを作成',
      creating: '作成中…',
      createAgent: '新しいエージェントを作成',
      addSalesExecutive: '追加',
      createAnother: '別のクエリを作成',
      retry: '再試行',
      addAgentNote: 'コメント・メモを追加',
      removeAgentNote: '削除',
    },
    states: {
      loadingReservationExecutives: '予約担当者を読み込み中…',
      loadingCountries: '国を読み込み中…',
      loadingAgents: 'エージェントを読み込み中…',
      resolvingSales: '営業担当を確認中…',
      salesSelected: '営業担当者を追加しました。',
      noReservationExecutives: '対象となるFIT/GROUP予約担当者がいません。',
    },
    validation: {
      reservationExecutive: '予約担当者は必須です。',
      country: '目的地の国は必須です。',
      currency: '有効な3文字の通貨を選択してください。',
      agent: 'エージェント会社は必須です。',
      salesExecutive: 'クエリ作成前に営業担当者を追加してください。',
      contactName: 'クエリ連絡先名は必須です。',
      startDate: '旅行開始日は必須です。',
      endDate: '旅行終了日は必須です。',
      dateRange: '旅行終了日は開始日以降にしてください。',
      travellerCount: '旅行者数は1以上の整数にしてください。',
      adultCount: '大人人数は1以上の整数にしてください。',
      childCount: '子供人数は0以上の整数にしてください。',
      childAges: '各子供の年齢を0～17で入力してください。',
      focCount: 'FOC人数は0以上の整数にしてください。',
      primaryTravellerName: '代表旅行者名は必須です。',
      primaryTravellerPhone: '代表旅行者の連絡先番号は必須です。',
      email: '連絡先メールアドレスは必須です。有効なアドレスを入力してください。',
    },
    errors: {
      reservationExecutives: '予約担当者を読み込めませんでした。',
      reservationExecutiveRequired: '有効な予約担当者を選択してください。',
      reservationExecutiveInvalid: '選択した予約担当者または所属チームは無効です。',
      create: 'クエリを作成できませんでした。入力内容は保持されています。',
      countries: '有効な国を読み込めませんでした。',
      agents: '有効なエージェントを読み込めませんでした。',
      salesResolution: '営業担当を確認できませんでした。入力内容を保持したまま再試行してください。',
      agentInactive: '選択したエージェントは無効です。別のエージェントを選択してください。',
      agentNotFound: '選択したエージェントは存在しません。',
      countryInactive: '選択した目的地の国は無効です。',
      countryNotDestination: '選択した国はクエリの目的地として有効ではありません。',
      countryNotFound: '選択した目的地の国は存在しません。',
      countryTimezone: '選択した国のタイムゾーン設定が無効です。',
      forbidden: 'このクエリを作成または変更する権限がありません。',
      email: 'クエリ連絡先メールが無効です。',
      date: '有効な旅行日を入力してください。',
      dateRange: '旅行終了日は開始日以降にしてください。',
      contactName: 'クエリ連絡先名は必須です。',
      travellerCount: '旅行者数は1以上の整数にしてください。',
      validation: 'クエリの入力内容を確認して再試行してください。',
      versionConflict: 'このクエリは他で変更されました。再読み込みしてから編集してください。',
      locked: '確定済み予約は直接変更できません。変更申請を作成してください。',
      notFound: 'クエリが見つかりません。',
    },
    messages: {
      created: 'クエリを作成しました: {number}',
    },
  },
  'zh-TW': {
    navigation: '建立查詢',
    eyebrow: '查詢',
    title: '建立查詢',
    description: '輸入開始新查詢所需的最少資料。',
    sections: {
      trip: '旅程基本資料',
      agent: '代理商',
      contact: '聯絡資訊',
      guest: '旅客資料',
      preferences: '偏好設定',
      additional: '其他資料',
    },
    transportationTypes: {
      private: '私人團 (Pvt)',
      sic: '併團 (SIC)',
    },
    fields: {
      reservationExecutive: '訂位專員',
      salesExecutive: '業務專員',
      transportationType: '交通方式',
      hotelStarCategory: '飯店星級',
      mealPreference: '餐食偏好',
      specialRemarks: '特殊備註',
      reservationCountry: '國家',
      reservationTeam: '團隊',
      country: '目的地國家',
      currency: '查詢幣別',
      startDate: '旅程開始日期',
      endDate: '旅程結束日期',
      travelDates: '旅程日期',
      travellerCount: '旅客人數',
      adultCount: '成人數',
      childCount: '兒童數',
      childAge: '第 {number} 位兒童年齡',
      focCount: 'FOC人數',
      totalTravellers: '旅客總數',
      numberOfNights: '住宿晚數',
      primaryTravellerName: '主要旅客姓名',
      primaryTravellerPhone: '主要旅客聯絡電話',
      bookingType: '預訂類型',
      agentSearch: '搜尋代理商',
      agent: '代理商公司',
      contactName: '聯絡人姓名',
      contactEmail: '電子郵件',
      contactPhone: '電話',
      notes: '備註',
      agentNote: '代理商意見／備註 {number}',
    },
    placeholders: {
      reservationExecutive: '選擇啟用中的訂位專員',
      reservationExecutiveSelf: '指派給自己',
      salesExecutive: '選擇啟用中的業務專員',
      salesSearch: '搜尋業務專員',
      reservationCountry: '選擇國家',
      reservationTeam: '選擇團隊',
      country: '選擇目的地國家',
      currency: '選擇幣別',
      agentSearch: '依代理商名稱或代碼搜尋',
      agent: '選擇啟用中的代理商',
      travelDates: '選擇旅程日期範圍',
      transportationType: '選擇交通方式',
      hotelStarCategory: '選擇飯店星級',
    },
    bookingTypes: {
      fit: 'FIT',
      group: '團體',
      help: '1–10位旅客為FIT；11位以上為團體。',
    },
    actions: {
      cancel: '取消',
      create: '建立查詢',
      creating: '建立中…',
      createAgent: '建立新代理商',
      addSalesExecutive: '新增',
      createAnother: '建立另一筆查詢',
      retry: '重試',
      addAgentNote: '新增意見／備註',
      removeAgentNote: '移除',
    },
    states: {
      loadingReservationExecutives: '正在載入訂位專員…',
      loadingCountries: '正在載入國家…',
      loadingAgents: '正在載入代理商…',
      resolvingSales: '正在解析業務負責人…',
      salesSelected: '已新增業務專員。',
      noReservationExecutives: '目前沒有符合資格的FIT/GROUP預訂專員。',
    },
    validation: {
      reservationExecutive: '訂位專員為必填。',
      country: '目的地國家為必填。',
      currency: '請選擇有效的三字母幣別。',
      agent: '代理商公司為必填。',
      salesExecutive: '建立查詢前必須解析並新增業務專員。',
      contactName: '查詢聯絡人姓名為必填。',
      startDate: '旅程開始日期為必填。',
      endDate: '旅程結束日期為必填。',
      dateRange: '旅程結束日期不得早於開始日期。',
      travellerCount: '旅客人數必須是正整數。',
      adultCount: '成人數必須是正整數。',
      childCount: '兒童數必須是零或正整數。',
      childAges: '請輸入每位兒童0至17歲的年齡。',
      focCount: 'FOC人數必須是零或正整數。',
      primaryTravellerName: '主要旅客姓名為必填。',
      primaryTravellerPhone: '主要旅客聯絡電話為必填。',
      email: '聯絡人電子郵件為必填，且必須有效。',
    },
    errors: {
      reservationExecutives: '無法載入訂位專員。',
      reservationExecutiveRequired: '請選擇啟用中的訂位專員。',
      reservationExecutiveInvalid: '所選訂位專員或所屬團隊已停用。',
      create: '無法建立查詢，已保留輸入資料。',
      countries: '無法載入啟用中的國家。',
      agents: '無法載入啟用中的代理商。',
      salesResolution: '無法解析業務負責人。您的輸入資料已保留，請重試。',
      agentInactive: '所選代理商已停用，請選擇其他代理商。',
      agentNotFound: '所選代理商已不存在。',
      countryInactive: '所選目的地國家已停用。',
      countryNotDestination: '所選國家未啟用為查詢目的地。',
      countryNotFound: '所選目的地國家已不存在。',
      countryTimezone: '所選國家的時區設定無效。',
      forbidden: '您沒有權限建立或變更此查詢。',
      email: '查詢聯絡人電子郵件無效。',
      date: '請輸入有效的旅程日期。',
      dateRange: '旅程結束日期不得早於開始日期。',
      contactName: '查詢聯絡人姓名為必填。',
      travellerCount: '旅客人數必須是正整數。',
      validation: '請檢查查詢資料後再試一次。',
      versionConflict: '此查詢已在其他地方變更，請重新載入後再編輯。',
      locked: '已確認的預訂不能直接修改，請建立修訂。',
      notFound: '找不到查詢。',
    },
    messages: {
      created: '已成功建立查詢：{number}',
    },
  },
  ko: {
    navigation: '쿼리 생성',
    eyebrow: '쿼리',
    title: '쿼리 생성',
    description: '새 쿼리를 시작하는 데 필요한 최소 정보를 입력합니다.',
    sections: {
      trip: '여행 기본 정보',
      agent: '에이전트',
      contact: '연락처 정보',
      guest: '여행자 정보',
      preferences: '선호 사항',
      additional: '추가 정보',
    },
    transportationTypes: {
      private: '프라이빗 (Pvt)',
      sic: '합류 (SIC)',
    },
    fields: {
      reservationExecutive: '예약 담당자',
      salesExecutive: '영업 담당자',
      transportationType: '교통 유형',
      hotelStarCategory: '호텔 성급',
      mealPreference: '식사 선호',
      specialRemarks: '특이 사항',
      reservationCountry: '국가',
      reservationTeam: '팀',
      country: '목적지 국가',
      currency: '쿼리 통화',
      startDate: '여행 시작일',
      endDate: '여행 종료일',
      travelDates: '여행 일정',
      travellerCount: '여행자 수',
      adultCount: '성인 수',
      childCount: '아동 수',
      childAge: '아동 {number} 나이',
      focCount: 'FOC 여행자 수',
      totalTravellers: '총 여행자 수',
      numberOfNights: '숙박 수',
      primaryTravellerName: '대표 여행자 이름',
      primaryTravellerPhone: '대표 여행자 연락처',
      bookingType: '예약 유형',
      agentSearch: '에이전트 검색',
      agent: '에이전트 회사',
      contactName: '연락처 이름',
      contactEmail: '이메일',
      contactPhone: '전화',
      notes: '메모',
      agentNote: '에이전트 의견/메모 {number}',
    },
    placeholders: {
      reservationExecutive: '활성 예약 담당자 선택',
      reservationExecutiveSelf: '나에게 배정',
      salesExecutive: '활성 영업 담당자 선택',
      salesSearch: '영업 담당자 검색',
      reservationCountry: '국가 선택',
      reservationTeam: '팀 선택',
      country: '목적지 국가 선택',
      currency: '통화 선택',
      agentSearch: '에이전트 이름 또는 코드로 검색',
      agent: '활성 에이전트 선택',
      travelDates: '여행 일정 범위 선택',
      transportationType: '교통 유형 선택',
      hotelStarCategory: '호텔 성급 선택',
    },
    bookingTypes: {
      fit: 'FIT',
      group: '그룹',
      help: '1~10명은 FIT, 11명 이상은 그룹입니다.',
    },
    actions: {
      cancel: '취소',
      create: '쿼리 생성',
      creating: '생성 중…',
      createAgent: '새 에이전트 생성',
      addSalesExecutive: '추가',
      createAnother: '다른 쿼리 생성',
      retry: '다시 시도',
      addAgentNote: '의견/메모 추가',
      removeAgentNote: '삭제',
    },
    states: {
      loadingReservationExecutives: '예약 담당자 불러오는 중…',
      loadingCountries: '국가 불러오는 중…',
      loadingAgents: '에이전트 불러오는 중…',
      resolvingSales: '영업 담당자를 확인하는 중…',
      salesSelected: '영업 담당자가 추가되었습니다.',
      noReservationExecutives: '사용 가능한 FIT/GROUP 예약 담당자가 없습니다.',
    },
    validation: {
      reservationExecutive: '예약 담당자는 필수입니다.',
      country: '목적지 국가는 필수입니다.',
      currency: '유효한 3자리 통화를 선택하세요.',
      agent: '에이전트 회사는 필수입니다.',
      salesExecutive: '쿼리를 생성하기 전에 영업 담당자를 확인하고 추가하세요.',
      contactName: '쿼리 연락처 이름은 필수입니다.',
      startDate: '여행 시작일은 필수입니다.',
      endDate: '여행 종료일은 필수입니다.',
      dateRange: '여행 종료일은 시작일 이후여야 합니다.',
      travellerCount: '여행자 수는 1 이상의 정수여야 합니다.',
      adultCount: '성인 수는 1 이상의 정수여야 합니다.',
      childCount: '아동 수는 0 이상의 정수여야 합니다.',
      childAges: '각 아동의 나이를 0~17로 입력하세요.',
      focCount: 'FOC 여행자 수는 0 이상의 정수여야 합니다.',
      primaryTravellerName: '대표 여행자 이름은 필수입니다.',
      primaryTravellerPhone: '대표 여행자 연락처는 필수입니다.',
      email: '연락처 이메일은 필수이며 올바른 형식이어야 합니다.',
    },
    errors: {
      reservationExecutives: '예약 담당자를 불러오지 못했습니다.',
      reservationExecutiveRequired: '활성 예약 담당자를 선택하세요.',
      reservationExecutiveInvalid: '선택한 예약 담당자 또는 소속 팀이 비활성 상태입니다.',
      create: '쿼리를 생성하지 못했습니다. 입력한 정보는 유지됩니다.',
      countries: '활성 국가를 불러오지 못했습니다.',
      agents: '활성 에이전트를 불러오지 못했습니다.',
      salesResolution: '영업 담당자를 확인하지 못했습니다. 입력 내용은 유지되며 다시 시도할 수 있습니다.',
      agentInactive: '선택한 에이전트가 비활성 상태입니다. 다른 에이전트를 선택하세요.',
      agentNotFound: '선택한 에이전트가 존재하지 않습니다.',
      countryInactive: '선택한 목적지 국가가 비활성 상태입니다.',
      countryNotDestination: '선택한 국가는 쿼리 목적지로 활성화되어 있지 않습니다.',
      countryNotFound: '선택한 목적지 국가가 존재하지 않습니다.',
      countryTimezone: '선택한 국가의 시간대 설정이 올바르지 않습니다.',
      forbidden: '이 쿼리를 생성하거나 변경할 권한이 없습니다.',
      email: '쿼리 연락처 이메일이 올바르지 않습니다.',
      date: '올바른 여행 날짜를 입력하세요.',
      dateRange: '여행 종료일은 시작일 이후여야 합니다.',
      contactName: '쿼리 연락처 이름은 필수입니다.',
      travellerCount: '여행자 수는 1 이상의 정수여야 합니다.',
      validation: '쿼리 정보를 확인한 후 다시 시도하세요.',
      versionConflict: '이 쿼리가 다른 곳에서 변경되었습니다. 새로고침 후 편집하세요.',
      locked: '확정된 예약은 직접 변경할 수 없습니다. 수정 요청을 생성하세요.',
      notFound: '쿼리를 찾을 수 없습니다.',
    },
    messages: {
      created: '쿼리가 생성되었습니다: {number}',
    },
  },
} as const;
